import React from 'react';
import type { PopulatedSection } from 'root/apiTypes';
import { st, classes } from './Section.st.css';
import { classes as global } from 'root/components/Menus/Global.st.css';
import { Items } from 'root/components/Menus/Items/Items';
import { Text, TextTypography, IconButton, WowImage, ImageResizeOptions } from 'wix-ui-tpa/cssVars';
import { dataHooks } from 'root/dataHooks';
import { useSectionLogic } from './Section.logic';
import { SECTION_ID, SECTION_TITLE_ID } from 'root/consts';
import { DesignedDivider } from 'root/components/Menus/DesignedDivider/DesignedDivider';

export const Section = ({ section, isFirstSection }: { section: PopulatedSection; isFirstSection?: boolean }) => {
  const {
    sectionTitleHtmlTag,
    sectionDescriptionHtmlTag,
    sectionCollapsed,
    setSectionCollapsed,
    icons,
    showSectionImage,
    t,
    sectionRef,
  } = useSectionLogic(section.id ?? '', isFirstSection);
  const sectionTitleId = SECTION_TITLE_ID(section.id!);

  return (
    <div
      ref={sectionRef}
      className={classes.sectionContainer}
      data-hook={dataHooks.section.container}
      id={SECTION_ID(section.id ?? '')}
      role="listitem"
    >
      <DesignedDivider className={st(classes.divider, classes.dividerTop)} dividerName="sectionTopDivider" />
      {section.image && (
        <WowImage
          key={showSectionImage ? section.image?.url : ''}
          shouldUseLQIP
          alt={section.image.altText ?? section.name}
          className={st(classes.sectionImage, sectionCollapsed ? classes.collapse : classes.expand)}
          data-hook={dataHooks.section.image}
          resize={ImageResizeOptions.cover}
          sourceHeight={section.image?.height}
          sourceWidth={section.image?.width}
          src={section.image?.url ?? ''}
        />
      )}
      <div className={classes.titleContainer} onClick={() => setSectionCollapsed(!sectionCollapsed)}>
        <Text
          className={st(global.fullWidth, classes.sectionTitle)}
          data-hook={dataHooks.section.name}
          id={sectionTitleId}
          tagName={sectionTitleHtmlTag}
          typography={TextTypography.smallTitle}
        >
          {section.name}
        </Text>
        <IconButton
          className={classes.sectionExpandCollapseIcon}
          icon={sectionCollapsed ? icons.collapsed : icons.expanded}
          aria-label={
            sectionCollapsed ? t('component.section.collapse.aria-label') : t('component.section.expand.aria-label')
          }
        />
      </div>
      {section.description && (
        <Text
          data-hook={dataHooks.section.description}
          tagName={sectionDescriptionHtmlTag}
          className={st(
            global.fullWidth,
            classes.sectionDescription,
            sectionCollapsed ? classes.collapse : classes.expand
          )}
        >
          {section.description}
        </Text>
      )}
      <DesignedDivider className={st(classes.divider, classes.dividerBottom)} dividerName="sectionBottomDivider" />
      <Items
        isSectionCollapsed={sectionCollapsed}
        items={section.items ?? []}
        sectionId={section.id ?? ''}
        sectionTitleId={sectionTitleId}
      />
    </div>
  );
};
